import { useState } from "react";
import { X, Trash2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { SERVICE_OPTIONS } from "./crewUtils";

const inputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2.5 text-sm text-[#23282B]";

function Field({ label, span2 = false, children }) {
  return (
    <label className={`block ${span2 ? "sm:col-span-2" : ""}`}>
      <span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function AssignmentForm({ initial, onClose, onSaved }) {
  const editing = Boolean(initial?.id);
  const [form, setForm] = useState({
    title: initial?.title || "",
    location: initial?.location || "",
    service_type: initial?.service_type || "Excavation",
    start_date: initial?.start_date || "",
    end_date: initial?.end_date || initial?.start_date || "",
    crew: (initial?.crew || []).join(", "),
    notes: initial?.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const payload = {
      ...form,
      end_date: form.end_date || form.start_date,
      crew: form.crew.split(",").map((c) => c.trim()).filter(Boolean),
    };
    try {
      if (editing) await api.patch(`/assignments/${initial.id}`, payload);
      else await api.post("/assignments", payload);
      await onSaved();
      onClose();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const markComplete = async () => {
    setBusy(true);
    try { await api.patch(`/assignments/${initial.id}`, { status: "complete" }); await onSaved(); onClose(); }
    catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this assignment?")) return;
    setBusy(true);
    try { await api.delete(`/assignments/${initial.id}`); onSaved(); onClose(); }
    catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="assignment-form-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg p-7 border-t-4 border-[#9CC4DB] my-auto">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl uppercase text-[#23282B]">{editing ? "Edit Assignment" : "New Assignment"}</div>
          <button type="button" onClick={onClose} data-testid="assignment-form-close" className="p-1 text-[#23282B]/60 hover:text-[#23282B]"><X size={20} /></button>
        </div>

        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Job / Title *" span2>
            <input data-testid="assignment-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="e.g. Maple Bay foundation dig" />
          </Field>
          <Field label="Location">
            <input data-testid="assignment-location" value={form.location} onChange={set("location")} className={inputCls} placeholder="e.g. Duncan, BC" />
          </Field>
          <Field label="Service">
            <select data-testid="assignment-service" value={form.service_type} onChange={set("service_type")} className={inputCls}>
              {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Start *">
            <input data-testid="assignment-start" required type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} />
          </Field>
          <Field label="End">
            <input data-testid="assignment-end" type="date" value={form.end_date} onChange={set("end_date")} className={inputCls} />
          </Field>
          <Field label="Crew (comma-separated)" span2>
            <input data-testid="assignment-crew" value={form.crew} onChange={set("crew")} className={inputCls} placeholder="e.g. Char, Mike, Danny" />
          </Field>
          <Field label="Notes" span2>
            <textarea data-testid="assignment-notes" rows={2} value={form.notes} onChange={set("notes")} className={inputCls} />
          </Field>
        </div>

        {error && <div data-testid="assignment-form-error" className="mt-4 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-700">{error}</div>}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button type="submit" disabled={busy} data-testid="assignment-form-submit" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors disabled:opacity-50">
            {busy ? "Saving…" : editing ? "Save Changes" : "Add to Calendar"}
          </button>
          {editing && initial.status !== "complete" && (
            <button type="button" onClick={markComplete} disabled={busy} data-testid="assignment-complete" className="inline-flex items-center gap-2 border-2 border-green-600 text-green-700 hover:bg-green-600 hover:text-white text-xs font-bold tracking-[0.12em] px-4 py-2.5 uppercase transition-colors">
              <CheckCircle2 size={14} /> Complete
            </button>
          )}
          {editing && (
            <button type="button" onClick={remove} disabled={busy} data-testid="assignment-delete" className="ml-auto inline-flex items-center gap-2 text-red-600 hover:text-red-700 text-xs font-bold tracking-[0.12em] uppercase">
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
